// App configuration types and constants
export type AppTier = "standard" | "premium";
export type Environment =
  | "localhost"
  | "development"
  | "staging"
  | "production";

export interface AppConfig {
  // Core app identity
  appName: string;
  appVersion: string;
  buildNumber: string;
  tier: AppTier;
  environment: Environment;

  // Feature flags based on tier
  features: {
    // Development tracking features
    separateMotorDomains: boolean; // Fine/Gross motor separation (premium)
    percentileTracking: boolean; // Development percentiles (premium)
    advancedMilestones: boolean; // Extended milestone library (premium)

    // Provider integration features
    healthcareProviderSync: boolean; // Sync with pediatricians (premium)
    schoolProviderSync: boolean; // Sync with schools/daycare (premium)
    multiProviderSupport: boolean; // Multiple provider connections (premium)

    // Family management features
    multipleChildren: boolean; // Multiple child profiles (both)
    familySharing: boolean; // Share data with family members (premium)
    exportReports: boolean; // Export development reports (premium)

    // Data and storage features
    cloudBackup: boolean; // Cloud data backup (premium)
    offlineMode: boolean; // Extended offline capabilities (both)
    dataExport: boolean; // Data export/import (premium)

    // UI and experience features
    customThemes: boolean; // Theme customization (premium)
    advancedCharts: boolean; // Advanced progress charts (premium)
    notifications: boolean; // Push notifications (both)

    // Auto-sync features
    autoSyncProfiles: boolean; // Automatic profile synchronization (both)
    autoSyncLevel: 1 | 2; // Auto-sync level: 1=app focus loss, 2=navigation (both)
    enableChangeAccount: boolean; // Allow changing account from My Account view (disabled by default)

    // Support and assistance
    prioritySupport: boolean; // Priority customer support (premium)
    expertConsultation: boolean; // Expert consultations (premium)
    detailedGuidance: boolean; // Detailed guidance content (premium)
  };

  // API and service configuration
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
    syncInterval: number; // milliseconds
  };

  // Storage configuration
  storage: {
    enableEncryption: boolean;
    maxCacheSize: number; // MB
    backupFrequency: number; // hours
  };

  // Development assessment configuration
  development: {
    devInitWindow: number; // Number of milestones below ceiling to set as floor (default: 4)
    devYesRequired: number; // Number of 'yes' milestones required to set floor (default: 2)
    devNotYetRequired: number; // Number of 'not-yet' milestones required to set ceiling (default: 2)
  };

  // Localization configuration
  localization: {
    languagetype: string; // Current language code: 'en', 'es', 'fr', 'de', 'zh', 'pt'
    supportedLanguages: string[]; // Array of supported language codes
  };

  // App-wide color palette
  colors: {
    blue: string; // Primary blue (#3498db)
    lightBlue: string; // Light blue variant
    green: string; // Success/positive green
    lightGreen: string; // Light green variant
    yellow: string; // Warning/attention yellow
    lightYellow: string; // Light yellow variant
    red: string; // Error/negative red
    lightRed: string; // Light red variant
  };

  // Analytics and monitoring
  analytics: {
    enabled: boolean;
    trackingId?: string;
    debugMode: boolean;
  };

  // Debugging and development
  debug: {
    enableLogging: boolean;
    logLevel: "error" | "warn" | "info" | "debug";
    showPerformanceMetrics: boolean;
  };
}

// Configuration type with all optional fields for partial configs
export interface PartialAppConfig {
  // Core app identity
  appName?: string;
  appVersion?: string;
  buildNumber?: string;
  tier?: AppTier;
  environment?: Environment;

  // Feature flags based on tier
  features?: Partial<AppConfig["features"]>;

  // API and service configuration
  api?: Partial<AppConfig["api"]>;

  // Storage configuration
  storage?: Partial<AppConfig["storage"]>;

  // Development assessment configuration
  development?: Partial<AppConfig["development"]>;

  // Localization configuration
  localization?: Partial<AppConfig["localization"]>;

  // App-wide color palette
  colors?: Partial<AppConfig["colors"]>;

  // Analytics and monitoring
  analytics?: Partial<AppConfig["analytics"]>;

  // Debugging and development
  debug?: Partial<AppConfig["debug"]>;
}

// Standard tier configuration
export const STANDARD_CONFIG: PartialAppConfig = {
  tier: "standard",
  features: {
    // Development tracking - basic features
    separateMotorDomains: false, // Combined motor domain
    percentileTracking: false, // No percentiles
    advancedMilestones: false, // Basic milestone library

    // Provider integration - limited
    healthcareProviderSync: false,
    schoolProviderSync: false,
    multiProviderSupport: false,

    // Family management - basic
    multipleChildren: true, // Unlimited children
    familySharing: false,
    exportReports: false,

    // Data and storage - basic
    cloudBackup: false,
    offlineMode: true, // Basic offline support
    dataExport: false,

    // UI and experience - basic
    customThemes: false,
    advancedCharts: false,
    notifications: true, // Basic notifications

    // Auto-sync - basic
    autoSyncProfiles: false, // Basic auto-sync (disabled by default)
    autoSyncLevel: 2, // Navigation-based sync
    enableChangeAccount: false, // Change account disabled by default

    // Support - community
    prioritySupport: false,
    expertConsultation: false,
    detailedGuidance: false,
  },

  storage: {
    enableEncryption: false,
    maxCacheSize: 50, // 50MB
    backupFrequency: 24, // Daily
  },

  api: {
    syncInterval: 300000, // 5 minutes
  },
};

// Premium tier configuration
export const PREMIUM_CONFIG: PartialAppConfig = {
  tier: "premium",
  features: {
    // Development tracking - advanced features
    separateMotorDomains: true, // Fine/Gross motor separation
    percentileTracking: true, // Percentile tracking
    advancedMilestones: true, // Extended milestone library

    // Provider integration - full support
    healthcareProviderSync: true,
    schoolProviderSync: true,
    multiProviderSupport: true,

    // Family management - advanced
    multipleChildren: true, // Unlimited children
    familySharing: true,
    exportReports: true,

    // Data and storage - advanced
    cloudBackup: true,
    offlineMode: true, // Extended offline support
    dataExport: true,

    // UI and experience - premium
    customThemes: true,
    advancedCharts: true,
    notifications: true,

    // Auto-sync - premium
    autoSyncProfiles: false, // Advanced auto-sync (disabled by default)
    autoSyncLevel: 2, // Navigation-based sync
    enableChangeAccount: false, // Change account disabled by default

    // Support - premium
    prioritySupport: true,
    expertConsultation: true,
    detailedGuidance: true,
  },

  storage: {
    enableEncryption: true,
    maxCacheSize: 200, // 200MB
    backupFrequency: 12, // Twice daily
  },

  api: {
    syncInterval: 60000, // 1 minute
  },
};

// Environment-specific base configurations
export const LOCALHOST_CONFIG: PartialAppConfig = {
  environment: "localhost",
  api: {
    baseUrl: "http://localhost:5162/api",
    timeout: 10000,
    retryAttempts: 3,
  },
  analytics: {
    enabled: false,
    debugMode: true,
  },
  debug: {
    enableLogging: true,
    logLevel: "debug",
    showPerformanceMetrics: true,
  },
};

export const DEVELOPMENT_CONFIG: PartialAppConfig = {
  environment: "development",
  api: {
    baseUrl: "https://wdcjsonwebdev.patienttools.com",
    timeout: 10000,
    retryAttempts: 3,
  },
  analytics: {
    enabled: false,
    debugMode: true,
  },
  debug: {
    enableLogging: true,
    logLevel: "debug",
    showPerformanceMetrics: true,
  },
};

export const STAGING_CONFIG: PartialAppConfig = {
  environment: "staging",
  api: {
    baseUrl: "https://wdcmkt.patienttools.com/wdcservices/api/v1",
    timeout: 15000,
    retryAttempts: 3,
  },
  analytics: {
    enabled: true,
    trackingId: "staging-analytics-id",
    debugMode: true,
  },
  debug: {
    enableLogging: true,
    logLevel: "info",
    showPerformanceMetrics: false,
  },
};

export const PRODUCTION_CONFIG: PartialAppConfig = {
  environment: "production",
  api: {
    baseUrl: "https://wdc.patienttools.com/wdcservices/api/v1",
    timeout: 20000,
    retryAttempts: 5,
  },
  analytics: {
    enabled: true,
    trackingId: "production-analytics-id",
    debugMode: false,
  },
  debug: {
    enableLogging: false,
    logLevel: "error",
    showPerformanceMetrics: false,
  },
};

// Configuration builder functions
export const createAppConfig = (
  tier: AppTier,
  environment: Environment,
  overrides: PartialAppConfig = {},
): AppConfig => {
  // Base configuration
  const baseConfig: AppConfig = {
    appName: "ChildNav",
    appVersion: "1.0.0",
    buildNumber: "1",
    tier,
    environment,
    features: {
      separateMotorDomains: false,
      percentileTracking: false,
      advancedMilestones: false,
      healthcareProviderSync: false,
      schoolProviderSync: false,
      multiProviderSupport: false,
      multipleChildren: true,
      familySharing: false,
      exportReports: false,
      cloudBackup: false,
      offlineMode: true,
      dataExport: false,
      customThemes: false,
      advancedCharts: false,
      notifications: true,
      autoSyncProfiles: false,
      autoSyncLevel: 2,
      enableChangeAccount: false,
      prioritySupport: false,
      expertConsultation: false,
      detailedGuidance: false,
    },
    api: {
      baseUrl: "",
      timeout: 15000,
      retryAttempts: 3,
      syncInterval: 300000,
    },
    storage: {
      enableEncryption: false,
      maxCacheSize: 50,
      backupFrequency: 24,
    },
    development: {
      devInitWindow: 4,
      devYesRequired: 2,
      devNotYetRequired: 2,
    },
    localization: {
      languagetype: "en",
      supportedLanguages: ["en", "es", "fr", "de", "zh", "pt"],
    },
    colors: {
      blue: "#3498db", // Primary blue (current MilestonesView color)
      lightBlue: "#85c1e9", // Light blue variant
      green: "#27ae60", // Success/positive green
      lightGreen: "#82e0aa", // Light green variant
      yellow: "#f39c12", // Warning/attention yellow
      lightYellow: "#f7dc6f", // Light yellow variant
      red: "#e74c3c", // Error/negative red
      lightRed: "#f1948a", // Light red variant
    },
    analytics: {
      enabled: false,
      debugMode: false,
    },
    debug: {
      enableLogging: false,
      logLevel: "error",
      showPerformanceMetrics: false,
    },
  };

  // Apply tier-specific configuration
  const tierConfig = tier === "premium" ? PREMIUM_CONFIG : STANDARD_CONFIG;

  // Apply environment-specific configuration
  // LOCALHOST_CONFIG    → local dev server (localhost:5162)
  // DEVELOPMENT_CONFIG  → pre-production remote server (wdcjsonwebdev.patienttools.com)
  // STAGING_CONFIG      → staging server (wdcmkt.patienttools.com)
  // PRODUCTION_CONFIG   → production server (wdc.patienttools.com)
  let envConfig: PartialAppConfig = {};
  switch (environment) {
    case "localhost":
      envConfig = LOCALHOST_CONFIG;
      break;
    case "development":
      envConfig = DEVELOPMENT_CONFIG;
      break;
    case "staging":
      envConfig = STAGING_CONFIG;
      break;
    case "production":
      envConfig = PRODUCTION_CONFIG;
      break;
  }

  // Deep merge configurations: base -> tier -> environment -> overrides
  return deepMerge(baseConfig, tierConfig, envConfig, overrides);
};

// Utility function for deep merging configuration objects
const deepMerge = (...objects: any[]): AppConfig => {
  const result: any = {};

  for (const obj of objects) {
    if (!obj) continue;

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          result[key] = deepMerge(result[key] || {}, value);
        } else {
          result[key] = value;
        }
      }
    }
  }

  return result as AppConfig;
};

// Feature checking utilities
export const hasFeature = (
  config: AppConfig,
  feature: keyof AppConfig["features"],
): boolean => {
  const value = config.features[feature];
  return typeof value === "boolean" ? value : false;
};

export const isPremium = (config: AppConfig): boolean => {
  return config.tier === "premium";
};

export const isStandard = (config: AppConfig): boolean => {
  return config.tier === "standard";
};

export const isDevelopment = (config: AppConfig): boolean => {
  return config.environment === "development";
};

export const isProduction = (config: AppConfig): boolean => {
  return config.environment === "production";
};

// Color access utility
export const getColors = (config: AppConfig) => {
  return config.colors;
};

// Individual color getters for convenience
export const getColor = (
  config: AppConfig,
  colorName: keyof AppConfig["colors"],
): string => {
  return config.colors[colorName];
};

/*
Usage examples:

// Import in components:
import { appConfig, getColor, getColors } from '../../config/configManager';

// Access all colors:
const colors = getColors(appConfig);

// Access individual colors:
const primaryBlue = getColor(appConfig, 'blue');
const successGreen = getColor(appConfig, 'green');

// Direct access:
const buttonColor = appConfig.colors.blue;

// In styles:
const styles = StyleSheet.create({
  button: {
    backgroundColor: appConfig.colors.blue,
  },
  successText: {
    color: getColor(appConfig, 'green'),
  },
});
*/

// Configuration validation
export const validateConfig = (
  config: AppConfig,
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate required fields
  if (!config.appName) errors.push("appName is required");
  if (!config.appVersion) errors.push("appVersion is required");
  if (!config.buildNumber) errors.push("buildNumber is required");
  if (!config.tier) errors.push("tier is required");
  if (!config.environment) errors.push("environment is required");

  // Validate API configuration
  if (!config.api.baseUrl && config.environment !== "development") {
    errors.push("api.baseUrl is required for non-development environments");
  }

  // Validate feature consistency
  if (config.tier === "standard" && config.features.separateMotorDomains) {
    errors.push("separateMotorDomains feature not available in standard tier");
  }

  if (config.tier === "standard" && config.features.percentileTracking) {
    errors.push("percentileTracking feature not available in standard tier");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};
